import { useEffect, useRef, useState } from "react";
import { streamAIInsights } from "@/lib/api";
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";

// Render markdown-ish content (### headings, - bullets, **bold**)
const renderNarrative = (text) => {
    const lines = text.split("\n");
    const nodes = [];
    let listBuf = [];
    const flushList = () => {
        if (listBuf.length) {
            nodes.push(<ul key={`ul-${nodes.length}`}>{listBuf.map((l, i) => <li key={i} dangerouslySetInnerHTML={{ __html: bold(l) }} />)}</ul>);
            listBuf = [];
        }
    };
    const bold = (s) => s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    for (const line of lines) {
        const t = line.trim();
        if (t.startsWith("### ")) { flushList(); nodes.push(<h3 key={`h-${nodes.length}`}>{t.slice(4)}</h3>); }
        else if (t.startsWith("- ") || t.startsWith("* ")) { listBuf.push(t.slice(2)); }
        else if (t.length === 0) { flushList(); }
        else { flushList(); nodes.push(<p key={`p-${nodes.length}`} dangerouslySetInnerHTML={{ __html: bold(t) }} />); }
    }
    flushList();
    return nodes;
};

export default function AIInsightsPanel({ context, contextLabel, autoLoad = true }) {
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const controllerRef = useRef(null);
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        if (!autoLoad || !context) return;
        // Abort any in-flight stream from a previous render/mount (fixes StrictMode double-invoke)
        controllerRef.current?.abort();
        const ctrl = new AbortController();
        controllerRef.current = ctrl;

        setText(""); setError(null); setLoading(true);
        streamAIInsights(
            context, null,
            (delta) => { if (!ctrl.signal.aborted) setText((t) => t + delta); },
            () => { if (!ctrl.signal.aborted) setLoading(false); },
            (err) => { if (!ctrl.signal.aborted) { setError(err); setLoading(false); } },
            ctrl.signal
        );

        return () => ctrl.abort();
    }, [context, autoLoad, reloadTick]);

    return (
        <div className="bg-fp-panel border-l-4 border-fp-ink p-8 h-full flex flex-col min-h-[540px]" data-testid="ai-insights-panel">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className="text-fp-ink" />
                    <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-secondary">Board Briefing · {contextLabel}</div>
                </div>
                <button data-testid="ai-refresh-btn" onClick={() => setReloadTick((t) => t + 1)} disabled={loading} className="text-fp-secondary hover:text-fp-ink transition-colors disabled:opacity-40" title="Regenerate">
                    <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
                </button>
            </div>

            <div className="narrative flex-1 overflow-y-auto pr-2">
                {error && (
                    <div className="font-sans text-sm text-fp-terracotta">{error}</div>
                )}
                {!error && !text && loading && (
                    <div className="space-y-2">
                        <div className="h-3 bg-fp-border w-3/4 animate-pulse" />
                        <div className="h-3 bg-fp-border w-2/3 animate-pulse" />
                        <div className="h-3 bg-fp-border w-1/2 animate-pulse" />
                        <div className="font-sans text-xs text-fp-muted mt-4">Analysing consolidated KPIs…</div>
                    </div>
                )}
                {text && renderNarrative(text)}
                {loading && text && <span className="inline-block w-2 h-4 bg-fp-ink ml-1 animate-pulse align-middle" />}
            </div>
        </div>
    );
}
